import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { useAppSelector } from 'app/hooks';
import DeadEndState from 'components/common/dead-end-state';
import InternalNote from 'components/layout/internal-note';
import { chooseSystemsEmptyMessages } from 'data/constants';
import { SystemOptionType } from 'data/types';
import { ValidSystemsDeadEndType } from 'types/dead-end';
import { useEffect, useMemo, useRef } from 'react';
import {
  buildDuctlessGridItems,
  orderDuctlessGridItems
} from './ductless-grid-items';
import { PickKind, TopPicks } from './equipment-comparison-utils';
import TopPickCard from './top-pick-card';

type DuctlessSystemsGridProps = Readonly<{
  picks: TopPicks;
  customSystems: SystemOptionType[];
  moreSystems: SystemOptionType[];
  systemNumberById: ReadonlyMap<string, number>;
  selectedId: string | null;
  onSelect: (system: SystemOptionType) => void;
  onUnselect: (system: SystemOptionType) => void;
  onAddToCompare: (system: SystemOptionType, kind: PickKind | null) => void;
  comparedIds: ReadonlySet<string>;
  rebateSystemIds: ReadonlySet<string>;
  showMore: boolean;
  onToggleShowMore: () => void;
  serverHasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  scrollRequest?: { id: string; nonce: number } | null;
  noMatchingSystems?: boolean;
  // Why the build produced nothing for the shown configurations (spec 001
  // T004). Rendered in place of the generic filters note when the list is
  // empty; absent when systems exist and the client-side filters hide them.
  deadEnd?: ValidSystemsDeadEndType;
}>;

export default function DuctlessSystemsGrid({
  picks,
  customSystems,
  moreSystems,
  systemNumberById,
  selectedId,
  onSelect,
  onUnselect,
  onAddToCompare,
  comparedIds,
  rebateSystemIds,
  showMore,
  onToggleShowMore,
  serverHasMore,
  isLoadingMore,
  onLoadMore,
  scrollRequest = null,
  noMatchingSystems = false,
  deadEnd
}: DuctlessSystemsGridProps) {
  const systemSort = useAppSelector((state) => state.ui.systemSort);

  const items = useMemo(
    () =>
      orderDuctlessGridItems(
        buildDuctlessGridItems({
          customSystems,
          picks,
          moreSystems,
          showMore,
          systemNumberById
        }),
        systemSort
      ),
    [customSystems, picks, moreSystems, showMore, systemNumberById, systemSort]
  );

  const cardNodesRef = useRef(new Map<string, HTMLDivElement>());
  const makeCardRef = (id: string) => (node: HTMLDivElement | null) => {
    if (node) {
      cardNodesRef.current.set(id, node);
    } else {
      cardNodesRef.current.delete(id);
    }
  };

  const lastScrollRequestNonceRef = useRef(0);
  useEffect(() => {
    if (!scrollRequest) return;
    if (scrollRequest.nonce === lastScrollRequestNonceRef.current) return;
    const node = cardNodesRef.current.get(scrollRequest.id);
    if (!node) return;
    lastScrollRequestNonceRef.current = scrollRequest.nonce;
    node.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  });

  const showRevealMore = moreSystems.length > 0 && !showMore;
  const showLoadMore = serverHasMore && !showRevealMore;

  const handleLoadMore = () => {
    if (!showMore) onToggleShowMore();
    onLoadMore();
  };

  return (
    <div
      id='system-list-rail'
      data-testid='ductless-systems-grid'
      className='flex min-w-0 flex-col gap-3 overflow-x-hidden'
    >
      {noMatchingSystems && deadEnd && <DeadEndState deadEnd={deadEnd} />}
      {noMatchingSystems && !deadEnd && (
        <InternalNote>
          {chooseSystemsEmptyMessages.noMatchingSystems}
        </InternalNote>
      )}
      <div className='grid min-w-0 gap-3 [grid-template-columns:repeat(auto-fit,minmax(min(100%,22rem),1fr))]'>
        {items.map(({ system, kind, systemNumber }) => (
          <div key={system.id} ref={makeCardRef(system.id)} className='min-w-0'>
            <TopPickCard
              system={system}
              kind={kind}
              isSelected={system.id === selectedId}
              isAddedToCompare={comparedIds.has(system.id)}
              showTopMatchBadge={kind === 'bestOverall'}
              onSelect={onSelect}
              onUnselect={onUnselect}
              onAddToCompare={onAddToCompare}
              hasRebates={rebateSystemIds.has(system.id)}
              systemNumber={systemNumber}
              viewMode='detailed'
              showCoverage
            />
          </div>
        ))}
      </div>
      {showRevealMore && (
        <button
          type='button'
          className='inline-flex items-center justify-center gap-1 self-center px-2 py-2 text-sm font-medium text-gray-600 hover:text-gray-900'
          onClick={onToggleShowMore}
        >
          Show more systems
          <ChevronDownIcon className='h-4 w-4' />
        </button>
      )}
      {showLoadMore && (
        <button
          type='button'
          disabled={isLoadingMore}
          className='inline-flex items-center justify-center gap-1 self-center px-2 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 disabled:cursor-not-allowed disabled:text-gray-400'
          onClick={handleLoadMore}
        >
          {isLoadingMore ? 'Loading more...' : 'Show more'}
        </button>
      )}
    </div>
  );
}
